import { Button } from "components/atoms/Button"
import { Spin } from "components/atoms/Spin"
import { Detail } from "components/atoms/detail/Detail"
import { DetailHeader } from "components/atoms/detail/DetailHeader"
import { DetailTable } from "components/atoms/detail/DetailTable"
import { DetailTableRow } from "components/atoms/detail/DetailTableRow"
import { DetailTitle } from "components/atoms/detail/DetailTitle"
import { deserializeTimestamp } from "deserializers/deserializeTimestamp"
import { useInitializeAccountData } from "hooks/useInitializeAccountData"
import { useAsyncLoading } from "hooks/useLoading"
import React, { useState, useEffect, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Link } from "react-router-dom"
import { useRecoilValue } from "recoil"
import styled from "styled-components"

import { RecruitStatus } from "../../../components/atoms/recruit"
import { EmploymentStatus } from "../../../components/const"
import { recruitsState } from "../../../recoils/recruitsState"

import type { Recruit } from "../../../interfaces/RecruitIF"

export const RecruitShowPage = (): JSX.Element => {
  const params = useParams()
  const [loading, asyncLoading] = useAsyncLoading("recruitShow")
  const navigate = useNavigate()
  const recruits = useRecoilValue<Recruit[]>(recruitsState)
  const [currentRecruit, setCurrentRecruit] = useState<Recruit | undefined>(
    undefined,
  )
  const initialize = useInitializeAccountData()
  const recruitId = params.id

  useEffect(() => {
    asyncLoading(initialize().catch((e) => console.warn(e)))
  }, [])

  useEffect(() => {
    const recruit = recruits.find((r) => r.recruitId === recruitId)
    if (!recruit && !loading) {
      navigate("/error/404")
      return
    }
    setCurrentRecruit(recruit)
  }, [recruitId, loading])

  const editPath = useMemo(() => {
    return `/recruit/${currentRecruit?.recruitId}/edit`
  }, [currentRecruit])

  const Images = useMemo(() => {
    const images = currentRecruit?.images
    if (!images || !images.length) return

    return (
      <ImageFlex>
        {images.map((image) => {
          return <img src={image} key={image} alt="" />
        })}
      </ImageFlex>
    )
  }, [currentRecruit])

  const Videos = useMemo(() => {
    const videoIds = currentRecruit?.videoIds
    if (!videoIds || !videoIds.length) return

    return (
      <ImageFlex>
        {videoIds.map((videoId: string, index: number) => {
          let url = `https://www.youtube.com/embed/${videoId}`
          if (!videoId) {
            url = (currentRecruit?.videos as string[])[index]
          }

          return videoId ? (
            <div key={videoId}>
              <iframe
                width="560"
                height="315"
                src={url}
                title="Move video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          ) : (
            <div>
              <a href={url} target="_blank" rel="noreferrer">
                {url}
              </a>
            </div>
          )
        })}
      </ImageFlex>
    )
  }, [currentRecruit])

  return loading || !currentRecruit ? (
    <Spin />
  ) : (
    <Detail>
      <DetailHeader>
        <DetailTitle>求人詳細</DetailTitle>
        <Link to={editPath}>
          <Button className="primary">編集</Button>
        </Link>
      </DetailHeader>
      <DetailTable>
        <DetailTableRow label="求人タイトル">
          {currentRecruit.title}
        </DetailTableRow>
        <DetailTableRow label="掲載状況">
          <RecruitStatus
            isApproved={currentRecruit.isApproved}
            isPublished={currentRecruit.isPublished}
          />
        </DetailTableRow>
        <DetailTableRow label="求人詳細">
          {currentRecruit.description}
        </DetailTableRow>
        <DetailTableRow label="募集開始日">
          {currentRecruit.recruitStartedAt &&
            deserializeTimestamp(currentRecruit.recruitStartedAt)}
        </DetailTableRow>
        <DetailTableRow label="航行区域">
          {currentRecruit.navigationArea}
        </DetailTableRow>
        <DetailTableRow label="内航/外航">
          {currentRecruit.seaRoute}
        </DetailTableRow>
        <DetailTableRow label="船種">
          {currentRecruit.shipTypeDetail
            ? currentRecruit.shipTypeDetail
            : currentRecruit.shipType}
        </DetailTableRow>
        <DetailTableRow label="職種">{currentRecruit.jobType}</DetailTableRow>
        <DetailTableRow label="乗組員数">
          {currentRecruit.crewNumber &&
            currentRecruit.crewNumber > 0 &&
            currentRecruit.crewNumber + "名"}
        </DetailTableRow>
        <DetailTableRow label="募集人数">
          {currentRecruit.recruitmentCount &&
            currentRecruit.recruitmentCount > 0 &&
            currentRecruit.recruitmentCount + "名"}
        </DetailTableRow>
        <DetailTableRow label="勤務地">
          {currentRecruit.workLocation}
        </DetailTableRow>
        <DetailTableRow label="勤務開始日">
          {currentRecruit.workStartedAt &&
            deserializeTimestamp(currentRecruit.workStartedAt)}
        </DetailTableRow>
        <DetailTableRow label="乗船履歴">
          {currentRecruit.yearsOnBoard}
        </DetailTableRow>
        <DetailTableRow label="必要な資格">
          {currentRecruit.requiredQualifications}
        </DetailTableRow>
        <DetailTableRow label="雇用期間">
          {currentRecruit.employmentTerm}
        </DetailTableRow>
        <DetailTableRow label="賃金手取月額">
          {currentRecruit.monthlySalary}
        </DetailTableRow>
        <DetailTableRow label="乗下船時給料の変動">
          {currentRecruit.salaryChangeable}
        </DetailTableRow>
        <DetailTableRow label="雇用形態">
          {currentRecruit.employmentStatus &&
            EmploymentStatus[currentRecruit.employmentStatus]}
        </DetailTableRow>
        <DetailTableRow label="会社の電話番号">
          {currentRecruit.telephoneNumber}
        </DetailTableRow>
        <DetailTableRow label="担当者の電話番号">
          {currentRecruit.contactTelephoneNumber}
        </DetailTableRow>
        <DetailTableRow label="ホームページURL">
          {currentRecruit.website && (
            <a target="_blank" href={currentRecruit.website} rel="noreferrer">
              {currentRecruit.website}
            </a>
          )}
        </DetailTableRow>
        <DetailTableRow label="メールアドレス">
          {currentRecruit.email}
        </DetailTableRow>
        <DetailTableRow label="求人イメージ画像">{Images}</DetailTableRow>
        <DetailTableRow label="求人イメージ動画">{Videos}</DetailTableRow>
        <DetailTableRow label="求人の非掲載設定">
          {currentRecruit.isPrivate ? "非掲載" : "掲載"}
        </DetailTableRow>
        <DetailTableRow label="作成日">
          {currentRecruit.createdAt &&
            deserializeTimestamp(currentRecruit.createdAt)}
        </DetailTableRow>
        <DetailTableRow label="最終更新日">
          {currentRecruit.updatedAt &&
            deserializeTimestamp(currentRecruit.updatedAt)}
        </DetailTableRow>
      </DetailTable>
    </Detail>
  )
}

const ImageFlex = styled.div`
  display: flex;
  align-items: center;
  img {
    margin-right: 20px;
    width: 30%;
  }
`
